import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import { dateTime, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Panel,
  Tile,
} from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Badge } from '../components/2b-status.js';
import { WorkerQueue } from './WorkerQueue.js';
import '../screens-2b.css';

interface LastRun {
  id: number;
  status: 'running' | 'ok' | 'failed' | 'abandoned';
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  items: number | null;
  error: string | null;
  workerId: string;
  trigger: 'schedule' | 'manual';
}
interface JobRow {
  name: string;
  label: string;
  description: string;
  cron: string;
  intervalMinutes: number | null;
  defaultMinutes: number | null;
  minMinutes: number;
  adjustable: boolean;
  nextRunAt: string;
  paused: boolean;
  lastRun: LastRun | null;
  lastOkAt: string | null;
  failureStreak: number;
  avgDurationMs: number | null;
  runs24h: number;
  failed24h: number;
}
interface JobsData {
  now: string;
  health: {
    workerAlive: boolean | null;
    lastSeenAt: string | null;
    workerVersion: string | null;
    failing: string[];
    failed24h: number;
  };
  queue: {
    available: boolean;
    queues: Record<string, { created: number; retry: number; active: number; failed: number }>;
  };
  failedQueue: Array<{
    id: string;
    name: string;
    retryCount: number;
    completedOn: string | null;
    error: string | null;
  }>;
  workers: Array<{
    worker_id: string;
    started_at: string;
    last_seen_at: string;
    version: string;
    uptimeSeconds: number;
  }>;
  failedInboxEvents: number;
  alertAfter: number;
  jobs: JobRow[];
}

const STATUS: Record<LastRun['status'], { label: string; tone: Tone }> = {
  ok: { label: 'v pořádku', tone: 'ok' },
  running: { label: 'běží', tone: 'acc' },
  failed: { label: 'selhala', tone: 'bad' },
  abandoned: { label: 'přerušena', tone: 'bad' },
};

const uptime = (s: number): string => {
  if (s < 90) return `${num(s)} s`;
  if (s < 5400) return `${num(Math.round(s / 60))} min`;
  if (s < 172_800) return `${num(Math.round(s / 3600))} h`;
  return `${num(Math.round(s / 86_400))} dní`;
};

const duration = (ms: number | null): string =>
  ms === null ? '—' : ms < 1000 ? `${num(ms)} ms` : `${(ms / 1000).toFixed(1).replace('.', ',')} s`;

/** Úlohy: dvě záložky, přehled úloh a Worker a fronta (D-302; ta čte jen databázi, takže funguje i bez workeru). */
export function Jobs({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const [tab, setTab] = useState<'jobs' | 'worker'>('jobs');
  return (
    <>
      <div className="seg" role="tablist" aria-label="Úlohy">
        <button
          role="tab"
          aria-selected={tab === 'jobs'}
          className={tab === 'jobs' ? 'on' : ''}
          onClick={() => setTab('jobs')}
        >
          Úlohy
        </button>
        <button
          role="tab"
          aria-selected={tab === 'worker'}
          className={tab === 'worker' ? 'on' : ''}
          onClick={() => setTab('worker')}
        >
          Worker a fronta
        </button>
      </div>
      {tab === 'jobs' ? <JobsList me={me} rights={rights} /> : <WorkerQueue me={me} />}
    </>
  );
}

function JobsList({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /* Co čeká na potvrzení: spuštění jedné úlohy nebo hromadné vrácení do fronty. */
  const [ask, setAsk] = useState<
    | { kind: 'run'; name: string; label: string }
    | { kind: 'retry-jobs' }
    | { kind: 'retry-events' }
    | null
  >(null);
  const canWrite = rights.write('jobs');
  const tz = me.defaultTimezone;

  const query = useQuery<JobsData>({
    queryKey: ['jobs'],
    queryFn: () => api.get('/api/jobs'),
    refetchInterval: 15_000,
  });
  const done = (text: string) => () => {
    setError(null);
    setNote(text);
    void client.invalidateQueries({ queryKey: ['jobs'] });
  };
  const fail = (err: Error): void => setError(err.message);
  const run = useMutation({
    mutationFn: (name: string) => api.post(`/api/jobs/${name}/run`),
    onSuccess: done('Úloha je zařazená, za chvíli se objeví nový běh.'),
    onError: fail,
  });
  const retryJobs = useMutation({
    mutationFn: (name?: string) =>
      api.post<{ retried: number }>('/api/jobs/retry-failed', name ? { name } : {}),
    onSuccess: (r) => done(`Vráceno do fronty: ${num(r.retried)}.`)(),
    onError: fail,
  });
  const retryEvents = useMutation({
    mutationFn: () => api.post<{ requeued: number }>('/api/data-quality/retry', {}),
    onSuccess: (r) => done(`Chybné události znovu ve frontě: ${num(r.requeued)}.`)(),
    onError: fail,
  });
  const setInterval_ = useMutation({
    mutationFn: (input: { name: string; minutes: number | null }) =>
      api.put(`/api/jobs/${input.name}/interval`, { minutes: input.minutes }),
    onSuccess: done('Interval úlohy je změněný, projeví se do minuty.'),
    onError: fail,
  });
  const pause = useMutation({
    mutationFn: (input: { name: string; paused: boolean }) =>
      api.post(`/api/jobs/${input.name}/${input.paused ? 'pause' : 'resume'}`),
    onSuccess: done('Plán úlohy je změněný.'),
    onError: fail,
  });

  if (query.isLoading) return <Loading what="úlohy" />;
  if (query.isError)
    return (
      <ErrorState
        title="Úlohy se nepodařilo načíst"
        error={query.error}
        onRetry={() => void query.refetch()}
      />
    );
  const data = query.data;
  if (!data) return <Loading />;

  const { health } = data;
  const queued = Object.values(data.queue.queues).reduce((s, q) => s + q.created + q.retry, 0);
  const failedInQueue = data.failedQueue.length;

  return (
    <>
      <PageHeader
        title="Úlohy"
        description="Co hub dělá na pozadí, kdy to naposledy proběhlo a jestli něco selhalo. Odsud jde úlohu spustit hned nebo vrátit selhané do fronty."
      />
      <div className="help">
        <p>
          <b>Pracovní proces (worker)</b> je část hubu, která úlohy spouští. Když se neozve déle než
          deset minut, hub vytvoří výjimku „Worker neodpovídá“. <b>Série selhání</b> počítá běhy po
          sobě; od {data.alertAfter}. selhání v řadě vznikne výjimka.
        </p>
        <p>
          Čeho si všímat: úloh se sérií selhání a hlášky „fronta zatím nevznikla“ (pracovní proces
          neběží).
        </p>
      </div>

      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
      {note ? (
        <p className="note" role="status">
          {note}
        </p>
      ) : null}

      <div className="tiles">
        <Tile
          label="Worker"
          value={
            health.workerAlive === null ? (
              <Badge tone="neu">neměří se</Badge>
            ) : health.workerAlive ? (
              <Badge tone="ok">odpovídá</Badge>
            ) : (
              <Badge tone="bad">neodpovídá</Badge>
            )
          }
          note={
            health.lastSeenAt
              ? `naposledy ${dateTime(health.lastSeenAt, tz)} · verze ${health.workerVersion ?? '—'}${data.workers[0] ? ` · běží ${uptime(data.workers[0].uptimeSeconds)}` : ''}`
              : 'zatím se neozval'
          }
          bad={health.workerAlive === false}
        />
        <Tile
          label="Ve frontě"
          value={num(queued)}
          note={data.queue.available ? 'čeká na spuštění' : 'fronta zatím nevznikla'}
        />
        <Tile label="Selhalo za 24 h" value={num(health.failed24h)} bad={health.failed24h > 0} />
        <Tile label="Selhané úlohy ve frontě" value={num(failedInQueue)} bad={failedInQueue > 0} />
        <Tile
          label="Chybné události"
          value={num(data.failedInboxEvents)}
          bad={data.failedInboxEvents > 0}
          note="příchozí data, ne úlohy"
        />
      </div>

      {canWrite ? (
        <Panel title="Obnovit frontu">
          <div className="row">
            <button
              type="button"
              className="btn"
              disabled={failedInQueue === 0 || retryJobs.isPending}
              onClick={() => setAsk({ kind: 'retry-jobs' })}
            >
              Zopakovat všechny selhané úlohy
            </button>
            {failedInQueue === 0 ? (
              <span className="note">Žádná úloha ve frontě nespadla.</span>
            ) : null}
            <button
              type="button"
              className="btn"
              disabled={data.failedInboxEvents === 0 || retryEvents.isPending}
              onClick={() => setAsk({ kind: 'retry-events' })}
            >
              Zpracovat znovu chybné události
            </button>
            {data.failedInboxEvents === 0 ? (
              <span className="note">Žádná příchozí událost nespadla.</span>
            ) : null}
          </div>
        </Panel>
      ) : null}

      <Panel title="Úlohy">
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Úloha</th>
                <th>Plán</th>
                <th>Poslední běh</th>
                <th>Stav</th>
                <th>Další spuštění</th>
                <th>Série selhání</th>
                <th>Průměrná doba</th>
                <th>Fronta</th>
                {canWrite ? <th>Akce</th> : null}
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((job) => {
                const q = data.queue.queues[job.name];
                const status = job.lastRun ? STATUS[job.lastRun.status] : null;
                return (
                  <tr key={job.name} data-testid={`job-${job.name}`}>
                    <td>
                      <b>{job.label}</b>
                      <details>
                        <summary>k čemu je</summary>
                        <p className="muted">{job.description}</p>
                        <p className="muted">
                          Technický název: <code>{job.name}</code>
                        </p>
                        {job.lastRun?.error ? (
                          <p className="muted">Chyba: {job.lastRun.error}</p>
                        ) : null}
                      </details>
                    </td>
                    <td className="s2b-nowrap">
                      {job.adjustable && canWrite ? (
                        <select
                          aria-label={`Interval úlohy ${job.label}`}
                          data-testid={`interval-${job.name}`}
                          value={
                            job.intervalMinutes === job.defaultMinutes
                              ? ''
                              : String(job.intervalMinutes)
                          }
                          onChange={(e) =>
                            setInterval_.mutate({
                              name: job.name,
                              minutes: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                        >
                          <option value="">výchozí (každých {job.defaultMinutes} min)</option>
                          {[1, 2, 5, 10, 15, 30, 60, 120, 360, 1440]
                            .filter((m) => m >= job.minMinutes && m !== job.defaultMinutes)
                            .map((m) => (
                              <option key={m} value={m}>
                                každých {m} min
                              </option>
                            ))}
                        </select>
                      ) : job.adjustable ? (
                        <>každých {job.intervalMinutes} min</>
                      ) : (
                        <>
                          <code>{job.cron}</code> <span className="muted">(čas UTC)</span>
                        </>
                      )}
                    </td>
                    <td>
                      {job.lastRun ? dateTime(job.lastRun.startedAt, tz) : '—'}
                      {job.lastRun?.trigger === 'manual' ? (
                        <span className="muted"> · ručně</span>
                      ) : null}
                      {job.lastRun?.items !== null && job.lastRun?.items !== undefined ? (
                        <div className="muted">{num(job.lastRun.items)} položek</div>
                      ) : null}
                    </td>
                    <td>
                      {job.paused ? (
                        <Badge tone="warn">pozastaveno</Badge>
                      ) : status ? (
                        <Badge tone={status.tone}>{status.label}</Badge>
                      ) : (
                        <Badge tone="neu">ještě neběžela</Badge>
                      )}
                    </td>
                    <td>{dateTime(job.nextRunAt, tz)}</td>
                    <td>
                      {job.failureStreak > 0 ? (
                        <Badge tone={job.failureStreak >= data.alertAfter ? 'bad' : 'warn'}>
                          {num(job.failureStreak)}×
                        </Badge>
                      ) : (
                        '0'
                      )}
                    </td>
                    <td>{duration(job.avgDurationMs)}</td>
                    <td>
                      {q
                        ? `${num(q.created + q.retry)} čeká · ${num(q.active)} běží · ${num(q.failed)} selhalo`
                        : '—'}
                    </td>
                    {canWrite ? (
                      <td>
                        <div className="row">
                          <button
                            type="button"
                            className="btn"
                            disabled={run.isPending}
                            aria-label={`Spustit teď: ${job.label}`}
                            onClick={() =>
                              setAsk({ kind: 'run', name: job.name, label: job.label })
                            }
                          >
                            Spustit teď
                          </button>
                          <button
                            type="button"
                            className="btn"
                            onClick={() => pause.mutate({ name: job.name, paused: !job.paused })}
                            disabled={pause.isPending}
                            aria-label={`${job.paused ? 'Obnovit plán' : 'Pozastavit plán'}: ${job.label}`}
                          >
                            {job.paused ? 'Obnovit plán' : 'Pozastavit plán'}
                          </button>
                          {q && q.failed > 0 ? (
                            <button
                              type="button"
                              className="btn"
                              aria-label={`Zopakovat selhané: ${job.label}`}
                              onClick={() => retryJobs.mutate(job.name)}
                            >
                              Zopakovat selhané
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Selhané úlohy ve frontě">
        {data.failedQueue.length === 0 ? (
          <EmptyState title="Nic nespadlo">Žádná úloha ve frontě neskončila chybou.</EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Úloha</th>
                  <th>Selhala</th>
                  <th>Pokusů</th>
                  <th>Chyba</th>
                </tr>
              </thead>
              <tbody>
                {data.failedQueue.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <code>{f.name}</code>
                    </td>
                    <td>{dateTime(f.completedOn, tz)}</td>
                    <td>{num(f.retryCount)}</td>
                    <td className="muted">{f.error ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Pracovní procesy (workery)">
        {data.workers.length === 0 ? (
          <EmptyState title="Žádný pracovní proces se zatím neozval">
            Dokud neběží, úlohy se nespouštějí a fronta se nezpracovává. Zkontrolujte, že je
            spuštěný kontejner hub-worker.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Proces</th>
                  <th>Naběhl</th>
                  <th>Běží</th>
                  <th>Naposledy se ozval</th>
                  <th>Verze</th>
                </tr>
              </thead>
              <tbody>
                {data.workers.map((w) => (
                  <tr key={w.worker_id}>
                    <td>
                      <code>{w.worker_id}</code>
                    </td>
                    <td>{dateTime(w.started_at, tz)}</td>
                    <td>{uptime(w.uptimeSeconds)}</td>
                    <td>{dateTime(w.last_seen_at, tz)}</td>
                    <td>{w.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <ConfirmDialog
        open={ask !== null}
        title={
          ask?.kind === 'run'
            ? `Spustit „${ask.label}“ hned?`
            : ask?.kind === 'retry-jobs'
              ? 'Zopakovat selhané úlohy?'
              : 'Zpracovat znovu chybné události?'
        }
        confirmLabel={ask?.kind === 'run' ? 'Spustit teď' : 'Vrátit do fronty'}
        busy={run.isPending || retryJobs.isPending || retryEvents.isPending}
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          if (ask?.kind === 'run') run.mutate(ask.name);
          else if (ask?.kind === 'retry-jobs') retryJobs.mutate(undefined);
          else if (ask?.kind === 'retry-events') retryEvents.mutate();
          setAsk(null);
        }}
      >
        {ask?.kind === 'run' ? (
          <p>
            Úloha se zařadí mimo plán a proběhne co nejdřív. Jestli kvůli tomu něco odejde
            zákazníkům, rozhoduje režim odesílání (při „Nanečisto“ neodejde nic) a vypínače zpráv.
          </p>
        ) : ask?.kind === 'retry-jobs' ? (
          <p>Do fronty se vrátí {num(failedInQueue)} selhaných úloh a zkusí se spustit znovu.</p>
        ) : (
          <p>
            Znovu se zpracuje {num(data.failedInboxEvents)} chybných příchozích událostí. Událost se
            nikdy nezpracuje dvakrát.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
